/*----------------------------------
- TLD INSIGHT ROUTE
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0054
 * @rule One Product read per page, its status answered by `resolveTldInsightAnswer` (`blocked`
 *       301 to the hub, `gone` 410, `not_public` 404, `unavailable` 503 with `Retry-After`). The
 *       slug comes from the path and the query is never read: the warmup serves this page from
 *       the static cache whatever the query.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { Gone, NotFound } from '@common/errors';
import type { TFetcher } from '@common/router/request/api';
import {
    TLD_HUB_PATH,
    formatTldLabel,
    tldInsightMarkdownPath,
    tldInsightPath,
    type PublicTldInsightResult,
} from '@uniquedomains/components/contracts/tlds/public';
import { buildPublicSearchConciseMarkdownPath } from '@uniquedomains/core/domains/public-search';
import { parseTldInsightPath } from '@uniquedomains/core/domains/tlds';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import { PublicPageUnavailable } from '@/client/pages/landing/lib/publicUnavailable';
import { buildTldInsightStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import TldInsightPage from '../page/insight/TldInsightPage';
import { resolveTldInsightAnswer } from './insightAnswer';

/*----------------------------------
- TYPES
----------------------------------*/

type TldInsightRouteRequest = {
    url: string;
    res?: {
        setHeader: (name: string, value: string) => void;
    };
};

type TldInsightRouteDataContext = {
    Product: {
        Navigation: { GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation> };
        Tlds: { GetPublicInsight: (data: { slug: string }) => TFetcher<PublicTldInsightResult> };
    };
    request: TldInsightRouteRequest;
};

type TldInsightRouteRenderContext = {
    navigation: LandingHeaderDomainsNavigation;
    nowIso: string;
    result: PublicTldInsightResult;
    page: {
        description?: string;
        head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
        jsonld: WebsiteJsonLdNode[];
        metas: Record<string, string | Date | null | undefined>;
        title?: string;
        url: string;
    };
    request: TldInsightRouteRequest;
    response?: {
        canonicalUrl?: URL;
        redirect?: (url: string, code?: number) => void;
        setHeaders?: (headers: Record<string, string>) => void;
    };
    Router: { url: (path: string) => string };
};

/*----------------------------------
- ROUTE
----------------------------------*/

export const buildTldInsightRouteData = ({ Product, request }: TldInsightRouteDataContext) => {
    // The route pattern keeps `/tld/applications` and dotted paths out; a token past 63
    // characters is no TLD either, answered here without a Product call.
    const slug = parseTldInsightPath(new URL(request.url, 'https://unique.domains').pathname);
    if (slug === null) throw new NotFound();

    return {
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
        result: Product.Tlds.GetPublicInsight({ slug }),
        // The render's clock, carried to hydration so both mark the same launch phase "Now". The
        // warmup re-renders the page within 22 to 30 h (or on a new insight stamp), so the marker
        // can lag a phase boundary by that much: accepted, every timeline row prints its dates.
        nowIso: new Date().toISOString(),
    };
};

export const renderTldInsightRoute = ({ navigation, nowIso, result, page, request, response, Router }: TldInsightRouteRenderContext) => {
    const answer = resolveTldInsightAnswer(result, 'page');
    if (answer.kind === 'redirect') {
        response?.redirect?.(Router.url(answer.path), 301);
        return null;
    }
    if (answer.kind === 'error') {
        if (answer.status === 410) throw new Gone();
        if (answer.status === 404) throw new NotFound();
        request.res?.setHeader('Retry-After', answer.retryAfter);
        response?.setHeaders?.({ 'Retry-After': answer.retryAfter });
        throw new PublicPageUnavailable(TLD_PAGE_CONTENT.insight.unavailable);
    }

    const { insight } = answer;
    const tld = formatTldLabel(insight.slug);
    const { meta } = TLD_PAGE_CONTENT.insight;
    const pageTitle = meta.pageTitle(tld);
    const metaDescription = meta.metaDescription(tld);
    const canonicalUrl = Router.url(tldInsightPath(insight.slug));

    page.title = pageTitle;
    page.description = metaDescription;
    page.url = canonicalUrl;
    page.metas = {
        ...page.metas,
        'og:description': metaDescription,
        'og:title': pageTitle,
        'og:type': 'website',
        'og:url': canonicalUrl,
        'twitter:card': 'summary_large_image',
        'twitter:description': metaDescription,
        'twitter:title': pageTitle,
        'twitter:url': canonicalUrl,
    };
    page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: meta.conciseMarkdownTitle,
        href: Router.url(buildPublicSearchConciseMarkdownPath('tld', insight.slug)),
    });
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: meta.fullMarkdownTitle,
        href: Router.url(tldInsightMarkdownPath(insight.slug)),
    });
    page.jsonld.push(
        ...buildTldInsightStructuredData({
            canonicalUrl,
            description: metaDescription,
            hubUrl: Router.url(TLD_HUB_PATH),
            insight,
            title: pageTitle,
        }),
    );
    if (response) response.canonicalUrl = new URL(canonicalUrl);

    return <TldInsightPage insight={insight} navigation={navigation} today={nowIso.slice(0, 10)} />;
};
